import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  TablePagination,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './QuestionsManagement.css';
import { LoaderWrapper } from '../../components/index';
import { 
  getQuestionsList, 
  addQuestion, 
  updateQuestion, 
  deleteQuestion,
  getStandardsByField,
  getCategoriesByFieldAndStandard,
  getSubCategoriesByCategoryId 
} from '../../services/api';

// Utility functions for field types (similar to other management components)
const getAllowedFieldTypes = (allFieldTypes) => {
  const storedLabTypeIds = localStorage.getItem('labTypeIds');
  const storedLabTypeId = localStorage.getItem('labTypeId');
  
  let allowedFieldTypes = [];
  
  if (storedLabTypeIds) {
    const allowedIds = storedLabTypeIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    // Map lab type IDs to field types
    allowedFieldTypes = allFieldTypes.filter(field => {
      if (allowedIds.includes(1) && field.value === 'Testing') return true;
      if (allowedIds.includes(3) && field.value === 'Medical') return true;
      if (allowedIds.includes(2) && field.value === 'Calibration') return true;
      if (allowedIds.includes(4) && field.value === 'PTP') return true;
      if (allowedIds.includes(5) && field.value === 'RMP') return true;
      return false;
    });
  } else if (storedLabTypeId) {
    const labTypeId = parseInt(storedLabTypeId);
    // Map single lab type ID to corresponding field type
    if (labTypeId === 1) allowedFieldTypes = allFieldTypes.filter(f => f.value === 'Testing');
    else if (labTypeId === 3) allowedFieldTypes = allFieldTypes.filter(f => f.value === 'Medical');
    else if (labTypeId === 2) allowedFieldTypes = allFieldTypes.filter(f => f.value === 'Calibration');
    else if (labTypeId === 4) allowedFieldTypes = allFieldTypes.filter(f => f.value === 'PTP');
    else if (labTypeId === 5) allowedFieldTypes = allFieldTypes.filter(f => f.value === 'RMP');
  }
  
  // If no specific mapping found or no localStorage data, return all field types except the placeholder
  if (allowedFieldTypes.length === 0) {
    allowedFieldTypes = allFieldTypes.filter(field => field.value !== '0');
  }
  
  return allowedFieldTypes;
};

const shouldDisableFieldDropdown = (allowedFieldTypes) => {
  return allowedFieldTypes.length === 1;
};

// Helper function to get default field type from localStorage
const getDefaultFieldType = () => {
  const storedLabTypeIds = localStorage.getItem('labTypeIds');
  const storedLabTypeId = localStorage.getItem('labTypeId');
  
  let defaultFieldType = '';
  
  if (storedLabTypeIds) {
    const allowedIds = storedLabTypeIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    if (allowedIds.length > 0) {
      const firstLabTypeId = allowedIds[0];
      if (firstLabTypeId === 1) defaultFieldType = 'Testing';
      else if (firstLabTypeId === 3) defaultFieldType = 'Medical';
      else if (firstLabTypeId === 2) defaultFieldType = 'Calibration';
      else if (firstLabTypeId === 4) defaultFieldType = 'PTP';
      else if (firstLabTypeId === 5) defaultFieldType = 'RMP';
    }
  } else if (storedLabTypeId) {
    const labTypeId = parseInt(storedLabTypeId);
    if (labTypeId === 1) defaultFieldType = 'Testing';
    else if (labTypeId === 3) defaultFieldType = 'Medical';
    else if (labTypeId === 2) defaultFieldType = 'Calibration';
    else if (labTypeId === 4) defaultFieldType = 'PTP';
    else if (labTypeId === 5) defaultFieldType = 'RMP';
  }
  
  return defaultFieldType;
};

const QuestionsManagement = () => {
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [standards, setStandards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Filter state
  const [filters, setFilters] = useState({
    feild: '',
    standard: '',
    category: '',
    subCategory: '',
    DisplayName: '',
    question: '',
  });
  
  // Form state
  const [formData, setFormData] = useState({
    field: '0',
    standard: '',
    categoryId: '',
    subCategoryId: '',
    displayId: '',
    DisplayName: '',
    question: '',
    isQHeading: 'No',
  });
  
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Form validation errors
  const [errors, setErrors] = useState({});

  const fieldTypes = [
    { value: '0', label: 'Select Field' },
    { value: 'Testing', label: 'Testing' },
    { value: 'Medical', label: 'Medical' },
    { value: 'Calibration', label: 'Calibration' },
    { value: 'PTP', label: 'PTP' },
    { value: 'RMP', label: 'RMP' },
  ];

  // Get filtered field types using utility (similar to other management components)
  const filteredFieldTypes = getAllowedFieldTypes(fieldTypes);

  // ReactQuill toolbar configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'align',
    'link'
  ];

  useEffect(() => {
    fetchQuestions();
  }, []);

  // Set initial field type from localStorage (similar to other management components)
  useEffect(() => {
    const storedLabTypeIds = localStorage.getItem('labTypeIds');
    const storedLabTypeId = localStorage.getItem('labTypeId');
    
    let defaultFieldType = '0';
    
    if (storedLabTypeIds) {
      const allowedIds = storedLabTypeIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      if (allowedIds.length > 0) {
        // Map first lab type ID to corresponding field type
        const firstLabTypeId = allowedIds[0];
        if (firstLabTypeId === 1) defaultFieldType = 'Testing';
        else if (firstLabTypeId === 3) defaultFieldType = 'Medical';
        else if (firstLabTypeId === 2) defaultFieldType = 'Calibration';
        else if (firstLabTypeId === 4) defaultFieldType = 'PTP';
        else if (firstLabTypeId === 5) defaultFieldType = 'RMP';
      }
    } else if (storedLabTypeId) {
      const labTypeId = parseInt(storedLabTypeId);
      if (labTypeId === 1) defaultFieldType = 'Testing';
      else if (labTypeId === 3) defaultFieldType = 'Medical';
      else if (labTypeId === 2) defaultFieldType = 'Calibration';
      else if (labTypeId === 4) defaultFieldType = 'PTP';
      else if (labTypeId === 5) defaultFieldType = 'RMP';
    }
    
    // Only set if we have a valid field type and it's different from current
    if (defaultFieldType !== '0' && defaultFieldType !== formData.field) {
      setFormData(prev => ({
        ...prev,
        field: defaultFieldType
      }));
    }
  }, []);

  // Initialize filter field type from localStorage
  useEffect(() => {
    const storedLabTypeIds = localStorage.getItem('labTypeIds');
    const storedLabTypeId = localStorage.getItem('labTypeId');
    
    let defaultFilterFieldType = '';
    
    if (storedLabTypeIds) {
      const allowedIds = storedLabTypeIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      if (allowedIds.length > 0) {
        const firstLabTypeId = allowedIds[0];
        if (firstLabTypeId === 1) defaultFilterFieldType = 'Testing';
        else if (firstLabTypeId === 3) defaultFilterFieldType = 'Medical';
        else if (firstLabTypeId === 2) defaultFilterFieldType = 'Calibration';
        else if (firstLabTypeId === 4) defaultFilterFieldType = 'PTP';
        else if (firstLabTypeId === 5) defaultFilterFieldType = 'RMP';
      }
    } else if (storedLabTypeId) {
      const labTypeId = parseInt(storedLabTypeId);
      if (labTypeId === 1) defaultFilterFieldType = 'Testing';
      else if (labTypeId === 3) defaultFilterFieldType = 'Medical';
      else if (labTypeId === 2) defaultFilterFieldType = 'Calibration';
      else if (labTypeId === 4) defaultFilterFieldType = 'PTP';
      else if (labTypeId === 5) defaultFilterFieldType = 'RMP';
    }
    
    // Set filter field type if we have a default and it's not already set
    if (defaultFilterFieldType && !filters.feild) {
      setFilters(prev => ({
        ...prev,
        feild: defaultFilterFieldType
      }));
    }
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [filters]);

  // Fetch standards when field changes
  useEffect(() => {
    if (formData.field && formData.field !== '0') {
      fetchStandards(formData.field);
    } else {
      setStandards([]);
      setFormData(prev => ({ ...prev, standard: '', categoryId: '', subCategoryId: '' }));
      setCategories([]);
      setSubCategories([]);
    }
  }, [formData.field]);

  // Fetch categories when standard changes
  useEffect(() => {
    if (formData.standard && formData.field && formData.field !== '0') {
      fetchCategories(formData.field, formData.standard);
    } else {
      setCategories([]);
      setFormData(prev => ({ ...prev, categoryId: '', subCategoryId: '' }));
      setSubCategories([]);
    }
  }, [formData.standard]);

  // Fetch sub-categories when category changes
  useEffect(() => {
    if (formData.categoryId) {
      fetchSubCategories(formData.categoryId);
    } else {
      setSubCategories([]);
      setFormData(prev => ({ ...prev, subCategoryId: '' }));
    }
  }, [formData.categoryId]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await getQuestionsList();
      if (response.data.success) {
        setQuestions(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      showSnackbar('Error fetching questions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStandards = async (field) => {
    try {
      const response = await getStandardsByField(field);
      if (response.data.success) {
        setStandards(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching standards:', error);
      showSnackbar('Error fetching standards', 'error');
    }
  };

  const fetchCategories = async (field, standard) => {
    try {
      const response = await getCategoriesByFieldAndStandard(field, standard);
      if (response.data.success) {
        setCategories(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      showSnackbar('Error fetching categories', 'error');
    }
  };

  const fetchSubCategories = async (categoryId) => {
    try {
      const response = await getSubCategoriesByCategoryId(categoryId);
      if (response.data.success) {
        setSubCategories(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching sub-categories:', error);
      showSnackbar('Error fetching sub-categories', 'error');
    }
  };

  // Memoized filtered questions - only recalculate when questions or filters change
  const filteredQuestions = useMemo(() => {
    let filtered = questions;

    if (filters.feild) {
      filtered = filtered.filter(q => q.feild === filters.feild);
    }

    if (filters.standard) {
      filtered = filtered.filter(q => q.standard === filters.standard);
    }

    if (filters.category) {
      filtered = filtered.filter(q => q.CatName === filters.category);
    }

    if (filters.subCategory) {
      filtered = filtered.filter(q => q.SubCategory === filters.subCategory);
    }

    if (filters.DisplayName) {
      filtered = filtered.filter(q => q.DisplayName === filters.DisplayName);
    }

    if (filters.question) {
      filtered = filtered.filter(q => 
        q.Question && q.Question.toLowerCase().includes(filters.question.toLowerCase())
      );
    }

    return filtered;
  }, [questions, filters]);

  // Paginated questions - only show current page
  const paginatedQuestions = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredQuestions.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredQuestions, page, rowsPerPage]);

  const clearFilters = useCallback(() => {
    setFilters({
      feild: getDefaultFieldType(),
      standard: '',
      category: '',
      subCategory: '',
      DisplayName: '',
      question: '',
    });
    setPage(0);
  }, []);

  // Get unique values for filter dropdowns - memoized
  const uniqueFields = useMemo(() => {
    const fields = [...new Set(questions.map(q => q.feild).filter(Boolean))];
    // Filter based on allowed field types
    const allowedFieldValues = filteredFieldTypes.map(f => f.value);
    return fields.filter(field => allowedFieldValues.includes(field));
  }, [questions, filteredFieldTypes]);

  const uniqueStandards = useMemo(() => {
    let standards = [...new Set(questions.map(q => q.standard).filter(Boolean))];
    
    if (filters.feild) {
      standards = [...new Set(
        questions
          .filter(q => q.feild === filters.feild)
          .map(q => q.standard)
          .filter(Boolean)
      )];
    }
    
    return standards;
  }, [questions, filters.feild]);

  const uniqueCategories = useMemo(() => {
    let categories = [...new Set(questions.map(q => q.CatName).filter(Boolean))];
    
    if (filters.standard) {
      categories = [...new Set(
        questions
          .filter(q => q.standard === filters.standard)
          .map(q => q.CatName)
          .filter(Boolean)
      )];
    }
    
    return categories;
  }, [questions, filters.standard]);

  const uniqueSubCategories = useMemo(() => {
    let subCategories = [...new Set(questions.map(q => q.SubCategory).filter(Boolean))];
    
    if (filters.category) {
      subCategories = [...new Set(
        questions
          .filter(q => q.CatName === filters.category)
          .map(q => q.SubCategory)
          .filter(Boolean)
      )];
    }
    
    return subCategories;
  }, [questions, filters.category]);

  const uniqueDisplayNames = useMemo(() => {
    return [...new Set(questions.map(q => q.DisplayName).filter(Boolean))];
  }, [questions]);

  const uniqueQuestions = useMemo(() => {
    return [...new Set(questions.map(q => q.Question).filter(Boolean))].slice(0, 100); // Limit to 100 for performance
  }, [questions]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.field || formData.field === '0') {
      newErrors.field = 'Field Type is required';
    }
    
    if (!formData.standard) {
      newErrors.standard = 'Standard is required';
    }
    
    // Check if question has actual content (not just empty HTML tags)
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = formData.question;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    if (!textContent.trim()) {
      newErrors.question = 'Question is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  }, [errors]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      const payload = {
        field: formData.field,
        standard: formData.standard,
        qCatId: formData.categoryId || null,
        qSubCatId: formData.subCategoryId || null,
        disId: formData.displayId.trim() || null,
        DisplayName: formData.DisplayName.trim() || null,
        question: formData.question, // Send HTML content as is
        isQHeading: formData.isQHeading,
      };
      
      const response = await addQuestion(payload);
      
      if (response.data.success) {
        showSnackbar('Question added successfully', 'success');
        resetForm();
        // Update local state instead of refetching all data
        setQuestions(prev => [...prev, response.data.data || payload]);
        setPage(0);
      } else {
        showSnackbar(response.data.message || 'Error adding question', 'error');
      }
    } catch (error) {
      console.error('Error adding question:', error);
      const errorMessage = error.response?.data?.message || 'Error adding question';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [formData]);

  const handleUpdate = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      const payload = {
        id: editId,
        field: formData.field,
        standard: formData.standard,
        qCatId: formData.categoryId || null,
        qSubCatId: formData.subCategoryId || null,
        disId: formData.displayId.trim() || null,
        DisplayName: formData.DisplayName.trim() || null,
        question: formData.question, // Send HTML content as is
        isQHeading: formData.isQHeading,
      };
      
      const response = await updateQuestion(payload);
      
      if (response.data.success) {
        showSnackbar('Question updated successfully', 'success');
        resetForm();
        // Update local state instead of refetching all data
        fetchQuestions();
        setQuestions(prev => prev.map(q => 
          (q.Id || q.id) === editId ? { ...q, ...payload } : q
        ));
      } else {
        showSnackbar(response.data.message || 'Error updating question', 'error');
      }
    } catch (error) {
      console.error('Error updating question:', error);
      const errorMessage = error.response?.data?.message || 'Error updating question';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [formData, editId]);

  const handleEdit = useCallback(async (question) => {
    console.log('Editing question:', question);
    
    let questionContent = question.Question || '';
    
    // Handle plain text questions
    if (questionContent && !questionContent.trim().startsWith('<')) {
      // Replace literal \n with actual line breaks (<br> tags)
      questionContent = questionContent.replace(/\\n/g, '<br>');
      // Replace actual newline characters with <br> tags
      questionContent = questionContent.replace(/\n/g, '<br>');
      // Wrap in paragraph tag
      questionContent = `<p>${questionContent}</p>`;
    }
    
    setFormData({
      field: question.feild,
      standard: question.standard,
      categoryId: question.QCatId,
      subCategoryId: question.QSubCatId,
      displayId: question.DisId || '',
      DisplayName: question.DisplayName || '',
      isQHeading: question.IsQHeading || 'No',
      // question: questionContent
    });
    setEditMode(true);
    setEditId(question.Id || question.id);
    
    // Fetch cascading dropdowns for edit mode
    if (question.feild && question.feild !== '0') { 
      console.log('Fetching cascading data for edit mode', question.feild);    
      await fetchStandards(question.feild);
      if (question.standard) {
        await fetchCategories(question.feild, question.standard);
        if (question.QCatId) {
          await fetchSubCategories(question.QCatId);
        }
      }
    }
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleDeleteClick = useCallback((id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      setLoading(true);
      const response = await deleteQuestion(deleteId);
      
      if (response.data.success) {
        showSnackbar('Question deleted successfully', 'success');
        // Update local state instead of refetching all data
         fetchQuestions();  
        setQuestions(prev => prev.filter(q => (q.Id || q.id) !== deleteId));
      } else {
        showSnackbar(response.data.message || 'Error deleting question', 'error');
      }
    } catch (error) {
      console.error('Error deleting question:', error);
      const errorMessage = error.response?.data?.message || 'Error deleting question';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  }, [deleteId]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setDeleteId(null);
  }, []);

  const resetForm = useCallback(() => {
    // Get default field type from localStorage like in form initialization
    const storedLabTypeIds = localStorage.getItem('labTypeIds');
    const storedLabTypeId = localStorage.getItem('labTypeId');
    
    let defaultFieldType = '0';
    
    if (storedLabTypeIds) {
      const allowedIds = storedLabTypeIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      if (allowedIds.length > 0) {
        const firstLabTypeId = allowedIds[0];
        if (firstLabTypeId === 1) defaultFieldType = 'Testing';
        else if (firstLabTypeId === 3) defaultFieldType = 'Medical';
        else if (firstLabTypeId === 2) defaultFieldType = 'Calibration';
        else if (firstLabTypeId === 4) defaultFieldType = 'PTP';
        else if (firstLabTypeId === 5) defaultFieldType = 'RMP';
      }
    } else if (storedLabTypeId) {
      const labTypeId = parseInt(storedLabTypeId);
      if (labTypeId === 1) defaultFieldType = 'Testing';
      else if (labTypeId === 3) defaultFieldType = 'Medical';
      else if (labTypeId === 2) defaultFieldType = 'Calibration';
      else if (labTypeId === 4) defaultFieldType = 'PTP';
      else if (labTypeId === 5) defaultFieldType = 'RMP';
    }

    setFormData({
      field: defaultFieldType,
      standard: '',
      categoryId: '',
      subCategoryId: '',
      displayId: '',
      DisplayName: '',
      question: '',
      isQHeading: 'No',
    });
    setEditMode(false);
    setEditId(null);
    setErrors({});
    setStandards([]);
    setCategories([]);
    setSubCategories([]);
  }, []);

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Pagination handlers
  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  if (loading) {
    return <LoaderWrapper loading={true} type="page" message="Loading..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#00024c' }}>
        {editMode ? 'Edit Question' : 'Add Question'}
      </Typography>

      {/* Form Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <form onSubmit={editMode ? handleUpdate : handleSubmit}>
          <Grid container spacing={3}>
            {/* Field Type */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                error={!!errors.field}
                required
              >
                <InputLabel>Field Type</InputLabel>
                <Select
                  name="field"
                  value={formData.field}
                  onChange={handleInputChange}
                  label="Field Type"
                  disabled={shouldDisableFieldDropdown(filteredFieldTypes)}
                >
                  <MenuItem value="0" disabled>
                    <em>Select Field</em>
                  </MenuItem>
                  {filteredFieldTypes.length > 0 ? (
                    filteredFieldTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <em>No Field Types Available</em>
                    </MenuItem>
                  )}
                </Select>
                {errors.field && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.field}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Standard */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                error={!!errors.standard}
                required
                disabled={!formData.field || formData.field === '0'}
              >
                <InputLabel>Standard</InputLabel>
                <Select
                  name="standard"
                  value={formData.standard}
                  onChange={handleInputChange}
                  label="Standard"
                >
                  <MenuItem value="">
                    <em>Select Standard</em>
                  </MenuItem>
                  {standards.map((std) => (
                    <MenuItem key={std.Id || std.id} value={std.standard || std.Standard}>
                      {std.standard || std.Standard}
                    </MenuItem>
                  ))}
                </Select>
                {errors.standard && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.standard}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Category Name */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                disabled={!formData.standard}
              >
                <InputLabel>Category Name</InputLabel>
                <Select
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleInputChange}
                  label="Category Name"
                >
                  <MenuItem value="">
                    <em>Select Category</em>
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.Id || cat.id} value={cat.Id || cat.id}>
                      {cat.CatName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Sub-Category Name */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                disabled={!formData.categoryId}
              >
                <InputLabel>Sub-Category Name</InputLabel>
                <Select
                  name="subCategoryId"
                  value={formData.subCategoryId}
                  onChange={handleInputChange}
                  label="Sub-Category Name"
                >
                  <MenuItem value="">
                    <em>Select Sub-Category</em>
                  </MenuItem>
                  {subCategories.map((subCat) => (
                    <MenuItem key={subCat.Id || subCat.id} value={subCat.Id || subCat.id}>
                      {subCat.SubCategory}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Display Id */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Display Id"
                name="displayId"
                value={formData.displayId}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Display Name"
                name="DisplayName"
                value={formData.DisplayName}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Is This Question Heading */}
            <Grid item xs={12} md={6}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Is This Question Heading?</FormLabel>
                <RadioGroup
                  row
                  name="isQHeading"
                  value={formData.isQHeading}
                  onChange={handleInputChange}
                >
                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Question */}
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Question <span style={{ color: 'red' }}>*</span>
                </Typography>
                <ReactQuill
                  theme="snow"
                  value={formData.question}
                  onChange={(content) => {
                    setFormData({ ...formData, question: content });
                    if (errors.question) {
                      setErrors({ ...errors, question: '' });
                    }
                  }}
                  modules={quillModules}
                  formats={quillFormats}
                  style={{
                    height: '200px',
                    marginBottom: '50px',
                  }}
                />
                {errors.question && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {errors.question}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color={editMode ? 'error' : 'primary'}
                  disabled={loading}
                >
                  {editMode ? 'Update' : 'Submit'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#e0ebeb',
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Questions List
          </Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchQuestions}
            size="small"
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>

        {/* Filter Section */}
        <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderBottom: '1px solid #e0e0e0' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <FilterListIcon sx={{ mr: 1, color: '#666' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                  Filter Questions
                </Typography>
              </Box>
            </Grid>
            
            {/* Field Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                size="small"
                options={uniqueFields}
                value={filters.feild || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({ ...prev, feild: newValue || '', standard: '', category: '', subCategory: '' }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Field" placeholder="Search" />
                )}
                clearOnEscape
                disabled={shouldDisableFieldDropdown(filteredFieldTypes)}
              />
            </Grid>

            {/* Standard Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                size="small"
                options={uniqueStandards}
                value={filters.standard || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({ ...prev, standard: newValue || '', category: '', subCategory: '' }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Standard" placeholder="Search" />
                )}
                clearOnEscape
              />
            </Grid>

            {/* Category Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                size="small"
                options={uniqueCategories}
                value={filters.category || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({ ...prev, category: newValue || '', subCategory: '' }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Category" placeholder="Search" />
                )}
                clearOnEscape
              />
            </Grid>

            {/* Sub-Category Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                size="small"
                options={uniqueSubCategories}
                value={filters.subCategory || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({ ...prev, subCategory: newValue || '' }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Sub-Category" placeholder="Search" />
                )}
                clearOnEscape
              />
            </Grid>

            {/* Display Id Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                size="small"
                options={uniqueDisplayNames}
                value={filters.DisplayName || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({ ...prev, DisplayName: newValue || '' }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Display Name" placeholder="Search" />
                )}
                clearOnEscape
              />
            </Grid>

            {/* Question Filter - Text search instead of dropdown for performance */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                size="small"
                label="Search Question"
                placeholder="Type to search..."
                value={filters.question || ''}
                onChange={(event) => {
                  setFilters(prev => ({ ...prev, question: event.target.value || '' }));
                }}
                fullWidth
              />
            </Grid>

            {/* Clear Filters Button */}
            <Grid item xs={12}>
              <Button
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                size="small"
                variant="outlined"
                color="secondary"
              >
                Clear All Filters
              </Button>
              <Typography variant="caption" sx={{ ml: 2, color: '#666' }}>
                Showing {filteredQuestions.length} of {questions.length} questions
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 80 }}>S.No</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}>Field/Standard</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 150 }}>Category</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 150 }}>Sub Category</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}>Display Name</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 300 }}>Question</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}>IsQHeading</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredQuestions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {questions.length === 0 ? 'No questions found' : 'No questions match the selected filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedQuestions.map((question, index) => (
                  <TableRow
                    key={question.Id || question.id}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell align="center">
                      <Typography variant="body2">
                        {page * rowsPerPage + index + 1}
                      </Typography>
                      <Tooltip title="Edit Question">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleEdit(question)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Question">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeleteClick(question.Id || question.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                      <Chip label={question.feild || 'N/A'} variant='outlined' size='small' color="primary" />
                      <Chip label={question.standard || 'N/A'} variant='outlined' size='small' color="secondary" />
                      </Box>
                    </TableCell>
                    <TableCell align="center">{question.CatName || 'N/A'}</TableCell>
                    <TableCell align="center">{question.SubCategory || 'N/A'}</TableCell>
                    <TableCell align="center">{question.DisplayName || 'N/A'}</TableCell>
                    <TableCell 
                      align="left" 
                      className="question-html-content"
                      dangerouslySetInnerHTML={{ 
                        __html: question.Question || 'N/A' 
                      }}
                    />
                    <TableCell align="center">{question.IsQHeading || 'N/A'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredQuestions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Questions per page:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this question?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuestionsManagement;
